import { defineStore } from 'pinia'
import { Ref, ref, watch } from 'vue'
import { setForcedOffline } from '@/utils/connectivity.ts'
import { ThemeName } from '@/stores/theme.ts'
import { CollectionFilterProps, ReleaseEntrySorts, ReleaseFilterProps, StartPage } from '@/utils'
import { Preferences } from '@capacitor/preferences'
import { Language, TranslationKey } from '@/stores/localization.ts'
import { ReleaseEntryTypeType } from '../../__generated__/graphql.ts'
import type { IconName } from '@/icons'
import {
  getDefaultDbSorts,
  getDefaultReleaseEntryFilter,
  getDefaultReleaseItemFilter,
  getDefaultVolumeSorts,
} from '@/utils/collection.ts'

const USER_LANGUAGE_PREFERENCE_KEY = 'userLanguagePreference'
const USER_THEME_PREFERENCE_KEY = 'userThemePreference'
const USER_START_PAGE_PREFERENCE_KEY = 'userStartPagePreference'
const CURRENT_AD_SCORE_KEY = 'currentAdScore'
const NOVELTIES_FILTER_KEY = 'noveltiesFilter'
const NOVELTIES_SORTING_KEY = 'noveltiesSorting'
const MY_LISTS_FILTER_KEY = 'myListsFilter'
const MY_LISTS_SORTING_KEY = 'myListsSorting'
const EXPLORATION_FILTER_KEY = 'explorationFilter'
const EXPLORATION_SORTING_KEY = 'explorationSorting'
const DATABASE_LISTS_FILTER_KEY = 'databaseListsFilter'
const DATABASE_LISTS_SORTING_KEY = 'databaseListsSorting'
const LISTS_STATUS_FILTER_KEY = 'listsStatusFilter'
const FORCE_OFFLINE_KEY = 'force-offline'
const CATALOG_SUBSCRIPTION_KEY = 'catalogSubscription'
const OFFLINE_IMAGE_TIER_KEY = 'offlineImageTier'
const OFFLINE_MODE_ENABLED_KEY = 'offlineModeEnabled'
const LIST_VIEW_MODE_KEY = 'listViewMode'
const PUSH_AUTO_ENABLE_ATTEMPTED_KEY = 'pushAutoEnableAttempted'

export type ListViewMode = 'gallery' | 'list' | 'compact'
export const LIST_VIEW_MODES = ['gallery', 'list', 'compact'] as const
export const LIST_VIEW_MODE_LABEL_MAP: Record<ListViewMode, TranslationKey> = {
  gallery: 'listViewModeGallery',
  list: 'listViewModeList',
  compact: 'listViewModeCompact',
}
export const LIST_VIEW_MODE_ICON_MAP: Record<ListViewMode, IconName> = {
  gallery: 'layout_grid',
  list: 'list',
  compact: 'align_justify',
}

// Which catalog slices the offline replica mirrors. Region is protocol-ready but
// hardcoded 'dach' until a real region field lands. Default = every medium type,
// so a fresh install replicates the full catalog (no behavior change vs. always-all).
export type CatalogSubscriptionPreference = {
  types: ReleaseEntryTypeType[]
  regions: string[]
}
export const ALL_CATALOG_SUBSCRIPTION_TYPES: ReleaseEntryTypeType[] =
  Object.values(ReleaseEntryTypeType)
const DEFAULT_CATALOG_SUBSCRIPTION: CatalogSubscriptionPreference = {
  types: ALL_CATALOG_SUBSCRIPTION_TYPES,
  regions: ['dach'],
}

// Which offline cover pack the user chose. `null` means the one-time chooser has
// not been answered yet; 'none' is an explicit "placeholders only" decision.
export type OfflineImageTier = '96' | '192' | '384' | 'none'

// Each group owns its own version. Bumping a group's `version` nukes only that
// group's keys on next init, leaving the other groups untouched. Bump the
// `filters` group whenever filter/sorting shapes change; the `settings` group
// holds rarely-changed user config that should survive filter resets.
const LEGACY_STORE_VERSION_KEY = 'storeVersion'
const LEGACY_LAST_STORE_VERSION = '14'

// One-time removal of keys retired from Preferences (e.g. the user collection
// blob, now cached in IndexedDB). Bump the version and extend the list to purge
// future leftovers. Runs once, independent of the grouped filter/settings nukes.
const LEGACY_PURGE_VERSION_KEY = 'storeVersion.legacyPurge'
const LEGACY_PURGE_VERSION = '2'
const LEGACY_PURGE_KEYS = ['userTmpCollectionEntries', 'compactListView']

type PreferenceGroup = {
  version: string
  versionKey: string
  keys: string[]
}

const PREFERENCE_GROUPS: PreferenceGroup[] = [
  {
    version: '3',
    versionKey: 'storeVersion.filters',
    keys: [
      NOVELTIES_FILTER_KEY,
      NOVELTIES_SORTING_KEY,
      MY_LISTS_FILTER_KEY,
      MY_LISTS_SORTING_KEY,
      EXPLORATION_FILTER_KEY,
      EXPLORATION_SORTING_KEY,
      DATABASE_LISTS_FILTER_KEY,
      DATABASE_LISTS_SORTING_KEY,
      LISTS_STATUS_FILTER_KEY,
    ],
  },
  {
    version: '1',
    versionKey: 'storeVersion.settings',
    keys: [
      USER_LANGUAGE_PREFERENCE_KEY,
      USER_THEME_PREFERENCE_KEY,
      USER_START_PAGE_PREFERENCE_KEY,
      CURRENT_AD_SCORE_KEY,
      FORCE_OFFLINE_KEY,
      CATALOG_SUBSCRIPTION_KEY,
      OFFLINE_IMAGE_TIER_KEY,
      OFFLINE_MODE_ENABLED_KEY,
      LIST_VIEW_MODE_KEY,
      PUSH_AUTO_ENABLE_ATTEMPTED_KEY,
    ],
  },
]

export type ThemePreference = ThemeName | 'automatic'

export const usePreferencesStore = defineStore('preferences', () => {
  const restorations: (() => Promise<void>)[] = []
  const [theme, setTheme] = createStringPreference<ThemePreference>(
    USER_THEME_PREFERENCE_KEY,
    'automatic',
    restorations,
  )
  const [startPage, setStartPage] = createStringPreference<StartPage>(
    USER_START_PAGE_PREFERENCE_KEY,
    '',
    restorations,
  )
  const [language, setLanguage] = createStringPreference<Language>(
    USER_LANGUAGE_PREFERENCE_KEY,
    'de',
    restorations,
  )
  const [currentAdScore, setCurrentAdScore] = createFloatPreference(
    CURRENT_AD_SCORE_KEY,
    0.0,
    restorations,
  )

  const [noveltiesFilter, setNoveltiesFilter] = createPreference(
    NOVELTIES_FILTER_KEY,
    getDefaultReleaseItemFilter(),
    restorations,
    (value) => {
      const { seriesTitle: _, ...rest } = value
      return JSON.stringify(rest)
    },
    (value) => ({
      ...getDefaultReleaseItemFilter(),
      ...(JSON.parse(value) as ReturnType<typeof getDefaultReleaseItemFilter>),
    }),
  )
  const [noveltiesSorting, setNoveltiesSorting] = createJsonPreference(
    NOVELTIES_SORTING_KEY,
    getDefaultVolumeSorts(),
    restorations,
  )
  const [myListsFilter, _setMyListsFilter] = createJsonPreference<
    Record<string, CollectionFilterProps>
  >(MY_LISTS_FILTER_KEY, {}, restorations)
  const [myListsSorting, _setMyListsSorting] = createJsonPreference<
    Record<string, ReleaseEntrySorts>
  >(MY_LISTS_SORTING_KEY, {}, restorations)

  const setMyListFilter = async (
    id: string,
    filter: CollectionFilterProps,
    defaults: CollectionFilterProps,
  ) => {
    const { seriesTitle: _, ...filterWithoutSearch } = filter
    const diff: CollectionFilterProps = {}
    for (const key of Object.keys(filterWithoutSearch) as (keyof typeof filterWithoutSearch)[]) {
      const value = filterWithoutSearch[key]
      if (value !== undefined && JSON.stringify(value) !== JSON.stringify(defaults[key])) {
        ;(diff as Record<string, unknown>)[key] = value
      }
    }
    await _setMyListsFilter({ ...myListsFilter.value, [id]: diff })
  }

  const setMyListSorting = async (id: string, sorting: ReleaseEntrySorts) => {
    await _setMyListsSorting({ ...myListsSorting.value, [id]: sorting })
  }

  const [explorationFilter, setExplorationFilter] = createPreference<ReleaseFilterProps>(
    EXPLORATION_FILTER_KEY,
    getDefaultReleaseEntryFilter(),
    restorations,
    (value) => {
      const { seriesTitle: _, ...rest } = value
      return JSON.stringify(rest)
    },
    (value) => JSON.parse(value) as ReleaseFilterProps,
  )
  const [explorationSorting, setExplorationSorting] = createJsonPreference<ReleaseEntrySorts>(
    EXPLORATION_SORTING_KEY,
    getDefaultDbSorts(),
    restorations,
  )

  const [databaseListsFilter, _setDatabaseListsFilter] = createJsonPreference<
    Record<string, ReleaseFilterProps>
  >(DATABASE_LISTS_FILTER_KEY, {}, restorations)
  const [databaseListsSorting, _setDatabaseListsSorting] = createJsonPreference<
    Record<string, ReleaseEntrySorts>
  >(DATABASE_LISTS_SORTING_KEY, {}, restorations)

  const setDatabaseListFilter = async (
    id: string,
    filter: ReleaseFilterProps,
    defaults: ReleaseFilterProps,
  ) => {
    const { seriesTitle: _, ...filterWithoutSearch } = filter
    const diff: ReleaseFilterProps = {}
    for (const key of Object.keys(filterWithoutSearch) as (keyof typeof filterWithoutSearch)[]) {
      const value = filterWithoutSearch[key]
      if (value !== undefined && JSON.stringify(value) !== JSON.stringify(defaults[key])) {
        ;(diff as Record<string, unknown>)[key] = value
      }
    }
    await _setDatabaseListsFilter({ ...databaseListsFilter.value, [id]: diff })
  }

  const setDatabaseListSorting = async (id: string, sorting: ReleaseEntrySorts) => {
    await _setDatabaseListsSorting({ ...databaseListsSorting.value, [id]: sorting })
  }
  const [listsStatusFilter, setListsStatusFilter] = createStringPreference<
    'collection' | 'read' | 'custom'
  >(LISTS_STATUS_FILTER_KEY, 'collection', restorations)
  const [forceOffline, setForceOffline] = createBooleanPreference(
    FORCE_OFFLINE_KEY,
    false,
    restorations,
  )
  // Mirror into the connectivity module so non-store consumers (retry links,
  // patched fetch) can read forced-offline synchronously.
  watch(forceOffline, (value) => setForcedOffline(value), { immediate: true })
  const [listViewMode, setListViewMode] = createStringPreference<ListViewMode>(
    LIST_VIEW_MODE_KEY,
    'gallery',
    restorations,
  )
  const [catalogSubscription, setCatalogSubscription] =
    createJsonPreference<CatalogSubscriptionPreference>(
      CATALOG_SUBSCRIPTION_KEY,
      DEFAULT_CATALOG_SUBSCRIPTION,
      restorations,
    )
  const [offlineImageTier, setOfflineImageTier] = createJsonPreference<OfflineImageTier | null>(
    OFFLINE_IMAGE_TIER_KEY,
    null,
    restorations,
  )
  // null = the one-time offline-mode consent prompt has not been answered yet.
  // true/false = the user's explicit opt-in/out. Gates the whole replica build.
  const [offlineModeEnabled, setOfflineModeEnabled] = createJsonPreference<boolean | null>(
    OFFLINE_MODE_ENABLED_KEY,
    null,
    restorations,
  )
  // True once the app has asked the OS for push permission on its own. Keeps the
  // first-start opt-in from re-prompting (and from re-registering a device the user
  // deliberately turned off in the notification settings).
  const [pushAutoEnableAttempted, setPushAutoEnableAttempted] = createBooleanPreference(
    PUSH_AUTO_ENABLE_ATTEMPTED_KEY,
    false,
    restorations,
  )

  const init = async () => {
    // Migration: pre-group installs used a single 'storeVersion' key. '14' was the
    // last value, meaning all keys were already valid. Seed each group's version so
    // the checks below pass and nothing gets nuked on first launch after this update.
    const legacyVersion = (await Preferences.get({ key: LEGACY_STORE_VERSION_KEY })).value
    if (legacyVersion === LEGACY_LAST_STORE_VERSION) {
      await Promise.all(
        PREFERENCE_GROUPS.map((group) =>
          Preferences.set({ key: group.versionKey, value: group.version }),
        ),
      )
      await Preferences.remove({ key: LEGACY_STORE_VERSION_KEY })
    }

    await Promise.all(
      PREFERENCE_GROUPS.map(async (group) => {
        const storedVersion = (await Preferences.get({ key: group.versionKey })).value
        if (storedVersion !== group.version) {
          await Promise.all(group.keys.map((key) => Preferences.remove({ key })))
          await Preferences.set({ key: group.versionKey, value: group.version })
        }
      }),
    )
    const purgeVersion = (await Preferences.get({ key: LEGACY_PURGE_VERSION_KEY })).value
    if (purgeVersion !== LEGACY_PURGE_VERSION) {
      await Promise.all(LEGACY_PURGE_KEYS.map((key) => Preferences.remove({ key })))
      await Preferences.set({ key: LEGACY_PURGE_VERSION_KEY, value: LEGACY_PURGE_VERSION })
    }

    await Promise.all(restorations.map((restore) => restore()))
  }

  return {
    init,
    language,
    theme,
    startPage,
    currentAdScore,
    setCurrentAdScore,
    setLanguage,
    setTheme,
    setStartPage,
    noveltiesFilter,
    noveltiesSorting,
    setNoveltiesFilter,
    setNoveltiesSorting,
    myListsFilter,
    myListsSorting,
    setMyListFilter,
    setMyListSorting,
    explorationFilter,
    explorationSorting,
    setExplorationFilter,
    setExplorationSorting,
    databaseListsFilter,
    databaseListsSorting,
    setDatabaseListFilter,
    setDatabaseListSorting,
    listsStatusFilter,
    setListsStatusFilter,
    forceOffline,
    setForceOffline,
    catalogSubscription,
    setCatalogSubscription,
    offlineImageTier,
    setOfflineImageTier,
    offlineModeEnabled,
    setOfflineModeEnabled,
    listViewMode,
    setListViewMode,
    pushAutoEnableAttempted,
    setPushAutoEnableAttempted,
  }
})

function createPreference<T>(
  preferenceKey: string,
  defaultValue: T,
  restorations: (() => Promise<void>)[] = [],
  serializer: (value: T) => string,
  deserializer: (value: string) => T,
): [Ref<T>, (value: T) => Promise<void>] {
  const valueRef = ref(defaultValue)
  restorations.push(async () => {
    try {
      const storedValue = (await Preferences.get({ key: preferenceKey })).value
      if (storedValue) {
        valueRef.value = deserializer(storedValue)
      }
    } catch (e) {
      console.error(e)
    }
  })
  return [
    valueRef as Ref<T>,
    async (value: T) => {
      valueRef.value = value
      await Preferences.set({ key: preferenceKey, value: serializer(value) })
    },
  ]
}

function createJsonPreference<T>(
  preferenceKey: string,
  defaultValue: T,
  restorations: (() => Promise<void>)[] = [],
): [Ref<T>, (value: T) => Promise<void>] {
  return createPreference<T>(
    preferenceKey,
    defaultValue,
    restorations,
    (value: T) => JSON.stringify(value),
    (value: string) => JSON.parse(value) as T,
  )
}

function createStringPreference<T>(
  preferenceKey: string,
  defaultValue: T,
  restorations: (() => Promise<void>)[] = [],
): [Ref<T>, (value: T) => Promise<void>] {
  return createPreference<T>(
    preferenceKey,
    defaultValue,
    restorations,
    (value: T) => value as string,
    (value: string) => value as T,
  )
}

function createFloatPreference(
  preferenceKey: string,
  defaultValue: number,
  restorations: (() => Promise<void>)[] = [],
): [Ref<number>, (value: number) => Promise<void>] {
  return createPreference<number>(
    preferenceKey,
    defaultValue,
    restorations,
    (value: number) => value.toString(),
    (value: string) => parseFloat(value),
  )
}

function createBooleanPreference(
  preferenceKey: string,
  defaultValue: boolean,
  restorations: (() => Promise<void>)[] = [],
): [Ref<boolean>, (value: boolean) => Promise<void>] {
  return createPreference<boolean>(
    preferenceKey,
    defaultValue,
    restorations,
    (value: boolean) => `${value}`,
    (value: string) => value === 'true',
  )
}
